import React, { useState, useMemo } from 'react';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { Typography } from '../../../../../Typography';
import { Box } from '../../../../../Box';
import {
  FileInputValue,
  MultipleFileInput,
  TextInput,
} from '../../../../../input';
import { useForm } from '../../hooks';

const MIN_IMAGE_WIDTH = 150;
const MIN_IMAGE_HEIGHT = 100;

interface AddImagesGalleryDialogProps {
  open: boolean;
  onClose: () => void;
  addImages: (files: FileInputValue[], description: string | undefined) => void;
}

interface AddImagesGalleryDialogForm {
  images: FileInputValue[];
}

export const AddImagesGalleryDialog = ({
  open,
  onClose,
  addImages,
}: AddImagesGalleryDialogProps) => {
  const [images, setImages] = useState<FileInputValue[]>([]);
  const [description, setDescription] = useState<string | undefined>();
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      images,
    };
  }, [images]);

  const fieldValidators = useMemo(() => {
    return {
      images: [
        validator.required<AddImagesGalleryDialogForm>(),
        validator.sizeLimit<AddImagesGalleryDialogForm>(
          'Загрузите файлы jpeg, png, webp, tiff, dng или heif не более 10МБ',
          {
            field: `image`,
            sizeLimit: 10,
          },
        ),
        validator.requiredIfFile<AddImagesGalleryDialogForm>(
          'Загрузите файлы jpeg, png, webp, tiff, dng или heif не более 10МБ',
          {
            field: 'image',
            fileTypes: [
              'image/jpeg',
              'image/png',
              'image/webp',
              'image/dng',
              'image/heif',
              'image/heic',
              'image/tiff',
            ],
          },
        ),
        validator.imageResolutionCheck(
          `Размер изображения не может быть меньше ${MIN_IMAGE_WIDTH} х ${MIN_IMAGE_HEIGHT}`,
          {
            minWidth: MIN_IMAGE_WIDTH,
            minHeight: MIN_IMAGE_HEIGHT,
          },
        ),
      ],
    };
  }, [validator]);

  const { errors, touched, validateField, hasValidationErrors, showError } =
    useForm<AddImagesGalleryDialogForm>(allValues, fieldValidators);

  const handleChangeImagesGalleryInput = async (value: FileInputValue[]) => {
    setImages(value);
    await validateField('images', value);
    showError('images');
  };

  const handleChangeDescriptionInput = (value?: string) => {
    setDescription(() => value || '');
  };

  const handleSubmit = () => {
    onClose();
    if (images) {
      addImages(images, description);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogContent
          title="Добавление галереи изображений"
          onClose={onClose}
          actions={
            <>
              <Button variant="tertiaryLight" size="large" onClick={onClose}>
                Отменить
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={
                  hasValidationErrors || images.length < 2 || images.length > 10
                }
              >
                Добавить
              </Button>
            </>
          }
        >
          <Box display="flex" flexDirection="column" gap="24">
            <Typography variant="body1" color="text.light.secondary">
              {`Необходимо добавить минимум 2 изображения.`}
              <br />
              {`Формат от 150 × 100
                  пикселей и вес до 10МБ.`}
              <br />
              {`Отображаться изображения будут в
                  соотношении сторон 16:9`}
            </Typography>

            <Box display="flex" flexDirection="column" gap="8">
              <MultipleFileInput
                required
                label="Изображения"
                value={images}
                description="Загрузите до 10 файлов с изображениями"
                dropzoneText="Загрузите до 10 файлов"
                onChange={handleChangeImagesGalleryInput}
                error={touched.images && !!errors.images}
                helperText={touched.images ? errors.images : undefined}
              />
              <TextInput
                name="description"
                label="Подпись"
                placeholder="Подпись к галерее изображений"
                onChange={handleChangeDescriptionInput}
              />
            </Box>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};
